import { useEffect, useId, useMemo, useRef, useState, type ReactElement } from 'react';
import { filterQuickReplies, type QuickReplyItem } from '../quick-replies';
import styles from './QuickRepliesPanel.module.css';

export type QuickRepliesState = 'loading' | 'error' | 'ready';

export interface QuickRepliesPanelProps {
  items: readonly QuickReplyItem[];
  state: QuickRepliesState;
  /** Administrador: a mensagem de "nenhuma resposta" diz onde cadastrar. */
  canManage: boolean;
  onRetry: () => void;
  onPick: (item: QuickReplyItem) => void;
  /** `restoreFocus`: voltar o foco ao campo de mensagem (Esc, escolha); falso quando o foco foi para outro lugar. */
  onClose: (restoreFocus: boolean) => void;
}

export const NO_REPLIES_TEXT = 'Nenhuma resposta rápida cadastrada ainda.';
export const NO_REPLIES_ADMIN_HINT = 'Cadastre em Administração → Respostas rápidas.';
export const NO_REPLIES_AGENT_HINT = 'Quem administra o CladvApp pode cadastrar.';
export const LOADING_TEXT = 'Carregando as respostas rápidas…';
export const LOAD_FAILED_TEXT = 'Não foi possível carregar as respostas rápidas.';
export const noMatchText = (query: string): string => `Nenhuma resposta combina com “${query}”.`;
export const countText = (n: number): string => `${n} ${n === 1 ? 'resposta' : 'respostas'}`;

/** O começo do texto, numa linha só (as quebras viram espaço). */
const preview = (body: string): string => body.replace(/\s+/g, ' ').trim().slice(0, 140);

/**
 * Lista de respostas rápidas com busca (docs/quick-replies.md §4), aberta pelo botão do campo de mensagem
 * ou pela tecla `/` com o campo vazio. Não é modal: o foco vai para a busca, as setas percorrem os
 * resultados (padrão _combobox + listbox_: o foco fica na busca e `aria-activedescendant` aponta o
 * resultado), `Enter` escolhe, `Esc` fecha. Quem chama insere o texto; nada é enviado daqui.
 */
export function QuickRepliesPanel({
  items,
  state,
  canManage,
  onRetry,
  onPick,
  onClose,
}: QuickRepliesPanelProps): ReactElement {
  const uid = useId();
  const searchId = `${uid}-search`;
  const listId = `${uid}-list`;
  const searchRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const results = useMemo(() => filterQuickReplies(items, query), [items, query]);
  const current = results.length === 0 ? -1 : Math.min(active, results.length - 1);
  const optionId = (i: number): string => `${uid}-option-${i}`;

  useEffect(() => searchRef.current?.focus(), []);
  // O resultado ativo acompanha a rolagem da lista.
  useEffect(() => {
    if (current >= 0)
      document.getElementById(optionId(current))?.scrollIntoView?.({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `optionId` só depende do `uid`
  }, [current]);

  const ready = state === 'ready';
  const empty = ready && items.length === 0;
  const noMatch = ready && !empty && results.length === 0;

  return (
    // O painel trata o que acontece DENTRO dele (Esc, o foco indo embora); quem usa o teclado opera
    // pela busca (combobox), que é o controle focável.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      role="dialog"
      aria-label="Respostas rápidas"
      className={styles.panel}
      onBlur={(event) => {
        // O foco saiu do painel para outro lugar: fecha sem puxar o foco de volta.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) onClose(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          onClose(true);
        }
      }}
    >
      <label htmlFor={searchId} className="sr-only">
        Buscar resposta rápida
      </label>
      <input
        ref={searchRef}
        id={searchId}
        type="text"
        role="combobox"
        aria-expanded={ready && results.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={current >= 0 ? optionId(current) : undefined}
        className={styles.search}
        placeholder="Buscar por atalho ou texto"
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setActive(0);
        }}
        onKeyDown={(event) => {
          if (event.nativeEvent.isComposing || event.keyCode === 229) return;
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (results.length === 0) return;
            const step = event.key === 'ArrowDown' ? 1 : -1;
            setActive((current + step + results.length) % results.length);
          } else if (event.key === 'Enter') {
            event.preventDefault();
            const chosen = results[current];
            if (chosen && !event.repeat) onPick(chosen);
          }
        }}
      />

      {state === 'loading' && (
        <p role="status" className={styles.note}>
          {LOADING_TEXT}
        </p>
      )}
      {state === 'error' && (
        <div className={styles.note}>
          <p role="alert">{LOAD_FAILED_TEXT}</p>
          <button type="button" className={styles.retry} onClick={onRetry}>
            Tentar de novo
          </button>
        </div>
      )}
      {empty && (
        <p role="status" className={styles.note}>
          {NO_REPLIES_TEXT} {canManage ? NO_REPLIES_ADMIN_HINT : NO_REPLIES_AGENT_HINT}
        </p>
      )}
      {noMatch && (
        <p role="status" className={styles.note}>
          {noMatchText(query.trim())}
        </p>
      )}
      {ready && results.length > 0 && (
        <>
          {/* A contagem é dita a cada busca; a lista em si é navegada pelas setas. */}
          <p role="status" className="sr-only">
            {countText(results.length)}
          </p>
          <ul id={listId} role="listbox" aria-label="Respostas rápidas" className={styles.list}>
            {results.map((item, index) => (
              // O teclado escolhe pela busca (setas e Enter, `aria-activedescendant`); o clique é só do mouse e do toque.
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <li
                key={item.id}
                id={optionId(index)}
                role="option"
                aria-selected={index === current}
                className={`${styles.option} ${index === current ? styles.optionActive : ''}`}
                // O clique não tira o foco da busca (senão o painel fecharia antes de escolher).
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => onPick(item)}
              >
                <span className={styles.shortcut} dir="ltr">
                  /{item.shortcut}
                </span>{' '}
                {/* O espaço separa o atalho do texto no nome acessível (visualmente são duas linhas). */}
                <span className={styles.preview} dir="auto">
                  {preview(item.body)}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
      {!ready && <ul id={listId} role="listbox" aria-label="Respostas rápidas" hidden />}
    </div>
  );
}
